import React from 'react';

import RecordBadge from '../common/RecordBadge';
import UserRef from './UserRef';
import useLinkedAccounts from '../../hooks/useLinkedAccounts';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { useCollectionContext } from '../../contexts/CollectionContext';
import { getItemDisplayValue } from '../../utils/items';
import { normalizeId } from '../../utils/ids';
import { userIdsOf } from '../../utils/userElements';

/**
 * A stored `user` value, read (docs/plans/element-sets-and-me.md, "Display
 * of a User value"): each account as the platform's UserRef chip (avatar +
 * name, hover card) — or, when the collection has an item LINKED to that
 * account (a people card with an account link), that item's RecordBadge in
 * the two-line item style, which is the door to the record. "Me points at
 * the staff record when there is one."
 *
 * Nothing is fetched for a value that is not made of ids: a public share
 * page receives display NAMES in place of the ids (the API replaces them),
 * and those print as plain text. `collectionId` defaults to the collection
 * the page is inside; a surface outside one (a modal, a test) passes it.
 *
 * Renders null for an empty value so every caller keeps its own em-dash.
 */
export default function UserValue({ element, value, collectionId = null, size = 'xs', className = '' }) {
	const context = useCollectionContext();
	const resolvedCollectionId = normalizeId(collectionId) || normalizeId(context?.resolvedCollectionId) || null;
	const cardDefinitions = useOptionalCardDefinitions();
	const ids = userIdsOf(value);
	const { linkedItemFor } = useLinkedAccounts(resolvedCollectionId, ids, { enabled: !!resolvedCollectionId && ids.length > 0 });

	if (value === undefined || value === null || value === '') return null;
	if (Array.isArray(value) && !value.length) return null;

	// Names, not ids: the public page's shape.
	if (!ids.length) {
		const text = Array.isArray(value) ? value.map(entry => String(entry ?? '')).filter(Boolean).join(', ') : String(value);
		return text ? <span className={['user-value', 'user-value--text', className].filter(Boolean).join(' ')}>{text}</span> : null;
	}

	const cardsById = cardDefinitions?.cardsById || {};
	const getElementsForCard = cardDefinitions?.getElementsForCard;

	return (
		<span className={['user-value', className].filter(Boolean).join(' ')} data-testid="user-value">
			{ids.map(id => {
				const item = linkedItemFor(id);
				if (item) {
					const card = cardsById[normalizeId(item.card_id)];
					const title = (typeof item.displayValue === 'string' && item.displayValue.trim())
						|| getItemDisplayValue({
							card,
							elementDefs: typeof getElementsForCard === 'function' ? getElementsForCard(item.card_id) : [],
							item,
						});
					return (
						<RecordBadge
							key={id}
							kind="item"
							record={card}
							kicker={card?.label?.singular || card?.name || undefined}
							title={title}
							size={size}
							to={`/collections/${resolvedCollectionId}/items/${normalizeId(item._id)}`}
							className="user-value__record"
						/>
					);
				}
				return <UserRef key={id} userId={id} size={20} />;
			})}
		</span>
	);
}
